import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import usePermissionCheck from 'utils/hooks/usePermissionCheck';
import useIsMobile from 'utils/hooks/useIsMobile';
import shortenedTabPath from 'helper/shortenedTabPath';
import CentralizedSpinner from 'components/CentralizedSpinner';
import TabbedPage from 'components/TabbedPage';
import RightSidebar from 'components/Layout/RightSidebar';
import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import SectionTabs, { SectionTabItem } from 'components/Layout/MobileShell/SectionTabs';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import ControlPanel from 'components/Windx/ControlPanel';
import BotaoDownload from 'components/Windx/BotaoDownload';
import { openRight } from 'redux/reducers/sidebarsReducer';
import {
  setTelaAtiva,
  setGranularidadeSH,
  sairDoWindx,
  WindxTela,
  WindxGranularidadeSH,
} from 'redux/reducers/windxControlPanelReducer';

function telaFromPath(pathname: string): WindxTela {
  // Ordem importante: paths mais especificos primeiro para nao casar
  // incorretamente o prefixo mais curto. FE-WINDX-047 (2026-05-27) moveu
  // a arvore Automatico para /windx/automatic/*.
  if (pathname.startsWith('/windx/automatic/semanal/comparativo')) return 'comparativo';
  if (pathname.startsWith('/windx/automatic/semanal')) return 'sm';
  return 'sh';
}

// FE-WINDX-050: sub-tab dentro de WEOL-SH. Default 'semi' para qualquer
// path que nao seja explicitamente /diario — inclui SM (granularidade so
// se aplica a SH, ignorada quando telaAtiva nao for 'sh').
function granularidadeFromPath(pathname: string): WindxGranularidadeSH {
  if (pathname.startsWith('/windx/automatic/diario')) return 'diario';
  return 'semi';
}

// Navegacao mobile em DOIS niveis, espelhando o roteador e o desktop: aqui
// ficam as abas de topo (WEOL-SH | WEOL-SM) e os sub-navs (Semi-horario|Diario
// e Semanal|Comparativo) continuam vindo de SemiHorarioLayout/SemanalLayout
// pelo <Outlet />.
//
// Achatar em 4 abas nao funciona: `shortenedTabPath` COLAPSA
// /windx/automatic/{semi-horario,diario} -> /windx/automatic e
// /windx/automatic/semanal/* -> /windx/automatic/semanal (para o TabbedPage do
// desktop acender). Como o SectionTabs marca a aba ativa por
// `tab.path === shortenedTabPath(pathname)`, abas achatadas nunca acenderiam.
const MOBILE_TABS: SectionTabItem[] = [
  { title: 'WEOL-SH', path: '/windx/automatic' },
  { title: 'WEOL-SM', path: '/windx/automatic/semanal' },
];

export default function WindxWrapper() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const keySaved = usePermissionCheck('windx');
  const isMobile = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    // No mobile nao ha RightSidebar — o painel vive num BottomSheet por tela.
    if (keySaved && !isMobile) dispatch(openRight());
  }, [dispatch, keySaved, isMobile]);

  // Mantem o painel sincronizado com a aba atual: ao trocar entre Weol-SH e
  // Weol-SM (incluindo o Comparativo, que vive sob /windx/semanal), o
  // ControlPanel deriva os filtros da `telaAtiva` no Redux. Em WEOL-SH a
  // sub-tab Semi-horário/Diário tambem entra via path (FE-WINDX-050).
  useEffect(() => {
    dispatch(setTelaAtiva(telaFromPath(location.pathname)));
    dispatch(setGranularidadeSH(granularidadeFromPath(location.pathname)));
  }, [dispatch, location.pathname]);

  // Sair do Windx (outra tela, logout) limpa filtros e consulta: ao voltar a
  // tela abre vazia, como no primeiro acesso.
  useEffect(() => () => {
    dispatch(sairDoWindx());
  }, [dispatch]);

  if (!keySaved) {
    return <CentralizedSpinner />;
  }

  if (isMobile) {
    const activeTab = MOBILE_TABS.find(
      (tab) => tab.path === shortenedTabPath(location.pathname),
    );
    // O download mora na barra de topo (uma acao do modulo, nao da consulta).
    // Comparativo nao tem ZIP — mesma regra do `showDownload={false}` que a
    // tela usava no desktop.
    const telaAtual = telaFromPath(location.pathname);
    const telaComDownload = telaAtual === 'sh' || telaAtual === 'sm' ? telaAtual : undefined;

    return (
      <>
        <MobileSectionHeader
          backLabel="WINDX"
          title={activeTab?.title ?? ''}
          onBack={() => navigate('/windx')}
          onAvatarClick={() => setMoreOpen(true)}
          action={telaComDownload && <BotaoDownload tela={telaComDownload} iconOnly />}
        />
        <SectionTabs tabs={MOBILE_TABS} sheetTitle="Telas do Windx" />
        <Outlet />
        <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
      </>
    );
  }

  return (
    <>
      <Row>
        <Col className="md-6">
          <h1 className="mb-3">WINDX</h1>
        </Col>
      </Row>
      <Row style={{ flexWrap: 'nowrap', paddingBottom: '3.3125rem' }}>
        <Col xs="auto" className="flex-fill overflow-hidden">
          <TabbedPage>
            <TabbedPage.Tab title="WEOL-SH" path="/windx/automatic" />
            <TabbedPage.Tab title="WEOL-SM" path="/windx/automatic/semanal" />
          </TabbedPage>
        </Col>
        <Col xs="auto">
          <RightSidebar>
            <ControlPanel />
          </RightSidebar>
        </Col>
      </Row>
      <div className="bottom-bar" />
    </>
  );
}
